import React, { useState, useEffect } from "react";
import BaseModal from "./BaseModal";
import { useSettings } from "../../providers/SettingsContext";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { settings, updateSettings } = useSettings();
  const [tempSettings, setTempSettings] = useState(settings);

  // Update temp settings when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSettings(settings);
    }
  }, [isOpen, settings]);

  const handleTextSizeChange = (textSize: number) => {
    setTempSettings({ ...tempSettings, textSize });
  };

  const handleVolumeChange = (volume: number) => {
    setTempSettings({ ...tempSettings, volume });
  };

  const handleChangeMicButtonVis = (showMicButton: boolean) => {
    setTempSettings({ ...tempSettings, showMicButton });
  };

  const handleApply = () => {
    updateSettings({
      textSize: tempSettings.textSize,
      volume: tempSettings.volume,
      showMicButton: tempSettings.showMicButton,
    });
    onClose();
  };

  const handleClose = () => {
    // Reset temp settings to current settings when closing without applying
    setTempSettings(settings);
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Settings"
      allowBackdropClose={true}
      showCloseButton={true}
      maxWidth="max-w-md"
      maxHeight="max-h-[85vh]"
      footerButtonText="Apply"
      onFooterButtonClick={handleApply}
    >
      {/* Settings Content */}
      <div className="space-y-8">
        {/* Text Size Section */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-[#46ACC6]"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h7"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">Text Size</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Font Size</span>
              <span className="text-sm font-medium text-[#46ACC6]">
                {tempSettings.textSize}px
              </span>
            </div>

            <div className="relative">
              <input
                type="range"
                min="12"
                max="24"
                step="1"
                value={tempSettings.textSize}
                onChange={(e) => handleTextSizeChange(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#46ACC6]"
                style={{
                  background: `linear-gradient(to right, #46ACC6 0%, #46ACC6 ${((tempSettings.textSize - 12) / (24 - 12)) * 100}%, #e5e7eb ${((tempSettings.textSize - 12) / (24 - 12)) * 100}%, #e5e7eb 100%)`,
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>12px</span>
                <span>24px</span>
              </div>
            </div>

            {/* Text Size Preview */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Preview:</p>
              <p
                className="text-gray-800 leading-relaxed"
                style={{ fontSize: `${tempSettings.textSize}px` }}
              >
                This is how your text will appear with the current size setting.
              </p>
            </div>
          </div>
        </div>

        {/* Volume Section */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-[#46ACC6]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <g data-name="audio">
                <path d="M11.46 3c-1 0-1 .13-6.76 4H1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3.7l5.36 3.57A2.54 2.54 0 0 0 14 18.46V5.54A2.54 2.54 0 0 0 11.46 3zM2 9h2v6H2zm10 9.46a.55.55 0 0 1-.83.45L6 15.46V8.54l5.17-3.45a.55.55 0 0 1 .83.45zM16.83 9.17a1 1 0 0 0-1.42 1.42 2 2 0 0 1 0 2.82 1 1 0 0 0 .71 1.71c1.38 0 3.04-3.62.71-5.95z" />
                <path d="M19 7.05a1 1 0 0 0-1.41 1.41 5 5 0 0 1 0 7.08 1 1 0 0 0 .7 1.7c1.61 0 4.8-6.05.71-10.19z" />
                <path d="M21.07 4.93a1 1 0 0 0-1.41 1.41 8 8 0 0 1 0 11.32 1 1 0 0 0 1.41 1.41 10 10 0 0 0 0-14.14z" />
              </g>
            </svg>
            <h3 className="text-lg font-medium text-gray-900">Audio Volume</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Volume Level</span>
              <span className="text-sm font-medium text-[#46ACC6]">
                {tempSettings.volume}%
              </span>
            </div>
            <div className="relative">
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={tempSettings.volume}
                onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#46ACC6]"
                style={{
                  background: `linear-gradient(to right, #46ACC6 0%, #46ACC6 ${tempSettings.volume}%, #e5e7eb ${tempSettings.volume}%, #e5e7eb 100%)`,
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Note: 100% volume may be very loud and may distort audio. Adjust
              to a comfortable level.
            </p>
          </div>
        </div>

        {/* Show/Hide Mic Button */}
        <div className="flex flex-row items-center space-x-2 pt-4">
          <input
            type="checkbox"
            id="show-mic-button"
            data-trust-trigger="play-responses"
            checked={tempSettings.showMicButton}
            onChange={(e) => {
              const checked = e.target.checked;
              handleChangeMicButtonVis(checked);
            }}
            className="w-4 h-4"
          />
          <label
            htmlFor="show-mic-button"
            className="text-xs sm:text-sm text-gray-600"
          >
            Show Mic Button
          </label>
        </div>
      </div>
    </BaseModal>
  );
};

export default SettingsModal;
